/**
 * ════════════════════════════════════════════════════════════════════════════
 *  PAYHIVE / AJOPAY — SHARED DESIGN SYSTEM  ("BrandTransfer")
 * ════════════════════════════════════════════════════════════════════════════
 *
 *  Single source of truth for the brand's visual language. Import TOKENS and
 *  components from here so every screen looks identical.
 *
 *  GOLDEN RULES (non-negotiable brand invariants)
 *   1. Currency is ALWAYS Naira "₦". Never "$"/USD anywhere.
 *   2. Primary brand colour is #1256C6 (hover #0E47A5, tint #EFF5FE).
 *   3. Content cards: radius rounded-2xl, border colour #f2f2f2.
 *   4. Primary buttons: #1256C6 bg, white text, h-12, rounded-lg, font-semibold.
 *   5. Money is shown with JetBrains-Mono (font-mono); everything else Outfit.
 *   6. Status colours: active→green, pending→orange, anything else→red.
 * ════════════════════════════════════════════════════════════════════════════
 */

import React, {CSSProperties, ReactNode} from "react";
import {AutoComplete, Input, Spin} from "antd";
import {Search, Coins} from "lucide-react";

/* ── 1. TOKENS ──────────────────────────────────────────────────────────── */

export const COLORS = {
  brand: "#1256C6",
  brandHover: "#0E47A5",
  brandTint: "#EFF5FE",

  white: "#FFFFFF",
  cardBorder: "#f2f2f2",
  surface: "#F9FAFB",
  surfaceAlt: "#f8fafc",

  text: "#111827",
  textMuted: "#6B7280",

  success: "#16A34A",
  successSoft: "#4ade80",
  warning: "#f59e0b",
  warningTint: "#FEF3C7",
  warningText: "#B45309",
  danger: "#dc2626",
};

export const GRADIENTS = {
  emerald: {tw: "bg-gradient-to-br from-emerald-500 to-emerald-600", stops: ["#10b981", "#059669"]},
  blue: {tw: "bg-gradient-to-br from-blue-500 to-blue-600", stops: ["#3b82f6", "#2563eb"]},
  purple: {tw: "bg-gradient-to-br from-purple-500 to-purple-600", stops: ["#a855f7", "#9333ea"]},
  orange: {tw: "bg-gradient-to-br from-orange-500 to-orange-600", stops: ["#f97316", "#ea580c"]},
  teal: {tw: "bg-gradient-to-br from-teal-500 to-teal-600", stops: ["#14b8a6", "#0d9488"]},
  bluePill: {tw: "bg-gradient-to-r from-blue-600 to-blue-700", stops: ["#2563eb", "#1d4ed8"]},
};

export const FONTS = {
  sans: "'Outfit', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  mono: "'JetBrains Mono', monospace",
};

export const TYPE = {
  size: {
    xs: "0.75rem", sm: "0.875rem", base: "1rem", lg: "1.125rem",
    xl: "1.25rem", "2xl": "1.5rem", "3xl": "1.875rem",
    "4xl": "2.25rem", "5xl": "3rem",
  },
  weight: {light: 300, normal: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800},
};

export const SPACE = {1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 8: 32, 10: 40, 12: 48};

export const RADIUS = {lg: 8, xl: 12, "2xl": 16, full: 9999};

export const SHADOW = {
  lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
};

export const CONTROL = {height: 44, inputRadius: RADIUS.lg, buttonHeight: 48};

/* ── 2. TAILWIND recipes ────────────────────────────────────────────────── */

export const TAILWIND = {
  page: "max-w-6xl mx-auto",
  card: "rounded-2xl",
  hero: "rounded-2xl p-6 md:p-8 text-white",
  heroTitle: "text-2xl md:text-3xl font-bold tracking-tight",
  heroSubtitle: "text-blue-100 mt-1",
  btnPrimary: "h-12 px-6 rounded-lg text-white text-base font-semibold border-0",
  btnOnHero: "font-semibold h-12 px-6 rounded-xl border-0",
  btnGhostOnHero: "text-white border-0",
  h1: "text-2xl md:text-3xl font-bold tracking-tight",
  h2: "text-lg font-semibold",
  label: "text-sm font-medium text-gray-700",
  muted: "text-gray-500 text-sm",
  amount: "font-mono",
  iconChip: "w-12 h-12 rounded-xl flex items-center justify-center",
  statGrid: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6",
};

/* ── 3. HELPERS ─────────────────────────────────────────────────────────── */

/** Format a number with thousands separators and the Naira sign. ALWAYS ₦. */
export function formatNaira(amount: number | string | null | undefined): string {
  const n = Number(amount);
  if (!n || Number.isNaN(n)) return "₦0";
  return "₦" + n.toLocaleString();
}

/** AntD-style Tag colour for a status. active→green, pending→orange, else red. */
export const statusColor = (status?: string): "green" | "orange" | "red" =>
  status === "active" ? "green" : status === "pending" ? "orange" : "red";

/** Risk dot Tailwind bg class. low→green, medium→yellow, else red. */
export const riskDotClass = (risk?: string): string =>
  risk === "low" ? "bg-green-500" : risk === "medium" ? "bg-yellow-500" : "bg-red-500";

/** Progress-bar fill colour by percentage. */
export const progressColor = (pct: number): string =>
  pct >= 80 ? COLORS.success : pct >= 60 ? COLORS.brand : pct >= 40 ? COLORS.warning : COLORS.danger;

type IconType = React.ComponentType<{className?: string; style?: CSSProperties}>;

/* ── 4. WEB COMPONENTS ──────────────────────────────────────────────────── */

/** Colored page banner with title, subtitle, and right-aligned actions. */
export function HeroHeader({
  icon: Icon,
  title,
  subtitle,
  actions,
}: {
  icon?: IconType;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="rounded-2xl p-6 md:p-8 text-white" style={{background: COLORS.brand}}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-4">
          {Icon && (
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{background: "rgba(255,255,255,0.18)"}}
            >
              <Icon className="w-6 h-6"/>
            </div>
          )}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-0 text-white">{title}</h1>
            {subtitle && <p className="text-blue-100 mt-1 mb-0">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex items-center gap-3">{actions}</div>}
      </div>
    </div>
  );
}

/** Standard content card: rounded-2xl + #f2f2f2 border. */
export function Card({
  children,
  className = "",
  style,
  ...props
}: {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`rounded-2xl bg-white border ${className}`}
      style={{borderColor: COLORS.cardBorder, ...style}}
      {...props}
    >
      {children}
    </div>
  );
}

/** Gradient stat / balance card — frosted icon chip, circle pattern, hover lift. */
export function StatCard({
  title,
  value,
  subtitle,
  status,
  icon: Icon = Coins,
  gradient = GRADIENTS.emerald.tw,
}: {
  title: ReactNode;
  value: ReactNode;
  subtitle?: ReactNode;
  status?: ReactNode;
  icon?: IconType;
  gradient?: string;
}) {
  return (
    <div
      className={`${gradient} dark:bg-gray-900 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6
                  text-white dark:text-gray-100 relative overflow-hidden group
                  hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl`}
    >
      <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 dark:bg-white/5
                      rounded-full -translate-y-12 sm:-translate-y-16 translate-x-12 sm:translate-x-16"/>
      <div className="absolute bottom-0 left-0 w-16 h-16 sm:w-24 sm:h-24 bg-white/5 dark:bg-white/10
                      rounded-full translate-y-8 sm:translate-y-12 -translate-x-8 sm:-translate-x-12"/>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2 sm:mb-3 lg:mb-4">
          <div className="p-1.5 sm:p-2 lg:p-3 bg-white/20 dark:bg-gray-800/40 rounded-lg sm:rounded-xl backdrop-blur-sm">
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6"/>
          </div>
          {status && (
            <div className="bg-white/20 dark:bg-gray-800/40 px-2 sm:px-3 py-1 rounded-full backdrop-blur-sm">
              <span className="text-xs sm:text-sm font-medium">{status}</span>
            </div>
          )}
        </div>
        <h3 className="text-white/80 dark:text-gray-300 text-xs sm:text-sm font-medium mb-1">{title}</h3>
        <p className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1 break-words">{value}</p>
        {subtitle && <p className="text-white/60 dark:text-gray-400 text-xs mb-0">{subtitle}</p>}
      </div>
    </div>
  );
}

/** Responsive container: 1 → 2 → auto-fit columns. Injects its own CSS. */
export function StatCardGrid({children}: {children?: ReactNode}) {
  return (
    <>
      <style>{`
        .ds-stat-grid { display: grid; grid-template-columns: 1fr; gap: .75rem; }
        @media (min-width:640px){ .ds-stat-grid{ grid-template-columns: repeat(2,1fr); gap:1rem; } }
        @media (min-width:1024px){ .ds-stat-grid{ grid-template-columns: repeat(auto-fit,minmax(220px,1fr)); gap:1.5rem; } }
      `}</style>
      <div className="ds-stat-grid">{children}</div>
    </>
  );
}

/** Loading placeholder matching the stat-card footprint. */
export function StatCardSkeleton({count = 4}: {count?: number}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
      {Array.from({length: count}, (_, i) => (
        <div key={i} className="bg-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 animate-pulse">
          <div className="h-5 sm:h-6 lg:h-8 bg-gray-300 rounded mb-2 sm:mb-3 lg:mb-4"/>
          <div className="h-6 sm:h-8 lg:h-12 bg-gray-300 rounded mb-2"/>
          <div className="h-3 sm:h-4 bg-gray-300 rounded"/>
        </div>
      ))}
    </div>
  );
}

/** Empty state: icon-in-circle, title, subtitle, optional CTA. */
export function EmptyState({
  icon: Icon = Coins,
  title,
  subtitle,
  action,
}: {
  icon?: IconType;
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="text-center py-12">
      <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon className="w-7 h-7" style={{color: COLORS.brand} as CSSProperties}/>
      </div>
      <h4 className="text-lg font-semibold text-gray-900 mb-1">{title}</h4>
      {subtitle && <p className="text-gray-500 text-sm mb-5 max-w-sm mx-auto">{subtitle}</p>}
      {action}
    </div>
  );
}

/** Solid brand-blue primary button. */
export function BrandButton({
  children,
  icon: Icon,
  className = "",
  ...props
}: {
  children?: ReactNode;
  icon?: IconType;
  className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg
                  text-white text-base font-semibold border-0 cursor-pointer transition-colors duration-200 ${className}`}
      style={{background: COLORS.brand}}
      onMouseEnter={(e) => (e.currentTarget.style.background = COLORS.brandHover)}
      onMouseLeave={(e) => (e.currentTarget.style.background = COLORS.brand)}
      {...props}
    >
      {Icon && <Icon className="w-4 h-4"/>}
      {children}
    </button>
  );
}

/** Pill status tag. Falls back to red for unknown statuses. */
export function StatusTag({status}: {status?: string}) {
  const palette = {
    green: {bg: "#dcfce7", fg: "#166534"},
    orange: {bg: "#ffedd5", fg: "#9a3412"},
    red: {bg: "#fee2e2", fg: "#991b1b"},
  }[statusColor(status)];
  return (
    <span
      className="font-medium px-3 py-1 rounded-full text-xs"
      style={{background: palette.bg, color: palette.fg}}
    >
      {String(status || "").toUpperCase()}
    </span>
  );
}

/** Standard search field — search-icon prefix, 44px tall to line up with selects. */
/**
 * The search box used across the admin app.
 *
 * It debounces. Pages wire search straight into a `useEffect` dependency, so the previous
 * raw input fired one request per keystroke — typing "payment" was seven round trips, and
 * because responses can land out of order, a slow early one could overwrite the result of
 * the last letter typed. Holding the typed text locally and emitting on a pause fixes the
 * request storm and that race together, without any page having to change.
 *
 * It also type-aheads: whatever a caller passes as `suggestions`, plus the searches that
 * actually returned something before, kept per `historyKey`.
 *
 * The emitted object carries `target.value`, which is what every call site reads.
 */
export function SearchField({
  className = "",
  value,
  onChange,
  debounceMs = 350,
  loading = false,
  suggestions,
  historyKey,
  placeholder,
  ...props
}: {
  className?: string;
  /** Extra type-ahead entries, e.g. values already on screen. */
  suggestions?: string[];
  /** Remembers successful searches under this key. Omit to keep no history. */
  historyKey?: string;
  loading?: boolean;
  debounceMs?: number;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const [text, setText] = React.useState(String(value ?? ""));
  const [open, setOpen] = React.useState(false);
  const timer = React.useRef<any>(null);
  const emitted = React.useRef(String(value ?? ""));

  // Follow the parent when it changes the value itself — a "clear all" on the toolbar, say —
  // without fighting the user mid-keystroke.
  React.useEffect(() => {
    const next = String(value ?? "");
    if (next !== emitted.current) {
      emitted.current = next;
      setText(next);
    }
  }, [value]);

  React.useEffect(() => () => clearTimeout(timer.current), []);

  const emit = React.useCallback((next: string) => {
    clearTimeout(timer.current);
    emitted.current = next;
    onChange?.({target: {value: next}, currentTarget: {value: next}} as any);

    if (historyKey && next.trim()) {
      try {
        const key = `payhive.search.${historyKey}`;
        const prior: string[] = JSON.parse(window.localStorage.getItem(key) || "[]");
        const merged = [next.trim(), ...prior.filter((item) => item !== next.trim())].slice(0, 6);
        window.localStorage.setItem(key, JSON.stringify(merged));
      } catch {
        /* Remembering a search term is never worth throwing over. */
      }
    }
  }, [historyKey, onChange]);

  const schedule = (next: string) => {
    setText(next);
    clearTimeout(timer.current);
    // Clearing the box should restore the full list at once; waiting feels broken.
    timer.current = setTimeout(() => emit(next), next === "" ? 0 : debounceMs);
  };

  const history: string[] = React.useMemo(() => {
    if (!historyKey) return [];
    try {
      return JSON.parse(window.localStorage.getItem(`payhive.search.${historyKey}`) || "[]");
    } catch {
      return [];
    }
  }, [historyKey, open]);

  const typed = text.trim().toLowerCase();
  const options = React.useMemo(() => {
    const seen = new Set<string>();
    return [...(suggestions || []), ...history]
      .filter((item) => item && typeof item === "string")
      .filter((item) => {
        const key = item.toLowerCase();
        if (seen.has(key) || key === typed) return false;
        seen.add(key);
        return !typed || key.includes(typed);
      })
      .slice(0, 8)
      .map((item) => ({value: item}));
  }, [suggestions, history, typed]);

  return (
    <div className={className}>
      <AutoComplete
        value={text}
        options={options}
        open={open && options.length > 0}
        onDropdownVisibleChange={setOpen}
        onSelect={(selected: string) => {
          setText(selected);
          emit(selected);
          setOpen(false);
        }}
        onChange={(next: string) => schedule(next ?? "")}
        style={{width: "100%"}}
      >
        <Input
          allowClear
          placeholder={placeholder as string}
          prefix={<Search className="w-4 h-4" style={{color: COLORS.textMuted}}/>}
          suffix={loading ? <Spin size="small"/> : <span/>}
          style={{height: CONTROL.height}}
          // Enter means "now", not "in 350ms".
          onPressEnter={() => emit(text)}
          {...(props as any)}
        />
      </AutoComplete>
    </div>
  );
}

const DesignSystem = {
  COLORS, GRADIENTS, FONTS, TYPE, SPACE, RADIUS, SHADOW, CONTROL, TAILWIND,
  formatNaira, statusColor, riskDotClass, progressColor,
  HeroHeader, Card, StatCard, StatCardGrid, StatCardSkeleton, EmptyState,
  BrandButton, StatusTag, SearchField,
};

export default DesignSystem;
